import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  alpha,
  createTheme,
  useTheme as useMuiTheme,
  Divider,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  People as PeopleIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  NotificationsActive as NotificationsIcon,
  Business as BusinessIcon,
  Palette as PaletteIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  BugReport as BugReportIcon,
  Storage as StorageIcon,
  Translate as TranslateIcon,
  Timer as TimerIcon,
  QrCode as QrCodeIcon,
  DisplaySettings as DisplaySettingsIcon,
  Print as PrintIcon,
  PieChart as PieChartIcon
} from '@mui/icons-material';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../context/ThemeContext';
import useSidebar from '../hooks/useSidebar';

const Configuracoes = () => {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const { isDarkMode } = useTheme();
  const baseTheme = useMuiTheme();
  const navigate = useNavigate();

  // Criando um tema personalizado
  const customTheme = createTheme({
    ...baseTheme,
    typography: {
      fontFamily: [
        'Montserrat',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
    },
  });

  // Opções de configuração
  const configOptions = [
    // Opções disponíveis agora
    {
      title: 'Usuários',
      description: 'Gerenciar usuários, cargos e permissões do sistema',
      icon: <PeopleIcon fontSize="large" />,
      path: '/usuarios',
      color: customTheme.palette.primary.main,
      disabled: false,
      category: 'atual'
    },
    {
      title: 'Serviços de Senhas',
      description: 'Configurar os tipos de serviços disponíveis para emissão de senhas',
      icon: <QrCodeIcon fontSize="large" />,
      path: '/configuracoes/servicos-senhas',
      color: '#00897b',
      disabled: false,
      category: 'atual'
    },
    {
      title: 'Painel de Chamada',
      description: 'Personalizar a exibição do painel de chamada de senhas',
      icon: <DisplaySettingsIcon fontSize="large" />,
      path: '/painel-configuracao',
      color: '#3949ab',
      disabled: false,
      category: 'atual'
    },
    {
      title: 'Segurança',
      description: 'Configurações de segurança, senhas e métodos de autenticação',
      icon: <SecurityIcon fontSize="large" />,
      path: '/seguranca',
      color: '#e91e63',
      disabled: true,
      category: 'breve'
    },
    {
      title: 'Aparência',
      description: 'Personalizar cores, temas e elementos visuais da aplicação',
      icon: <PaletteIcon fontSize="large" />,
      path: '/branding',
      color: '#9c27b0',
      disabled: false,
      category: 'atual'
    },
    {
      title: 'Configuração de Tickets',
      description: 'Configure layout, informações e personalize completamente os tickets de senhas impressos',
      icon: <PrintIcon fontSize="large" />,
      path: '/configuracao-tickets',
      color: '#E53935',
      disabled: false,
      category: 'atual'
    },
    {
      title: 'Notificações',
      description: 'Gerenciar alertas, avisos e notificações do sistema',
      icon: <NotificationsIcon fontSize="large" />,
      path: '/notificacoes',
      color: '#ff9800',
      disabled: true,
      category: 'breve'
    },
    {
      title: 'Dados da Empresa',
      description: 'Editar informações da empresa, logotipos e dados institucionales',
      icon: <BusinessIcon fontSize="large" />,
      path: '/empresa',
      color: '#4caf50',
      disabled: true,
      category: 'breve'
    },
    {
      title: 'Idioma e Região',
      description: 'Configurar idiomas e formatos regionais',
      icon: <TranslateIcon fontSize="large" />,
      path: '/idioma',
      color: '#3f51b5',
      disabled: true,
      category: 'futuro'
    },
    {
      title: 'Banco de Dados',
      description: 'Gerenciar backups e restauração de dados',
      icon: <StorageIcon fontSize="large" />,
      path: '/banco-dados',
      color: '#795548',
      disabled: true,
      category: 'futuro'
    },
    {
      title: 'Tempo de Atendimento',
      description: 'Configurar limites e alertas de tempo de atendimento',
      icon: <TimerIcon fontSize="large" />,
      path: '/tempo-atendimento',
      color: '#009688',
      disabled: true,
      category: 'futuro'
    },
    {
      title: 'Sistema',
      description: 'Configurações avançadas e informações do sistema',
      icon: <SettingsIcon fontSize="large" />,
      path: '/sistema',
      color: '#607d8b',
      disabled: true,
      category: 'futuro'
    },
  ];

  const handleOptionClick = (path, disabled) => {
    if (!disabled) {
      navigate(path);
    }
  };

  // Filtrar opções por categoria
  const opcoesAtuais = configOptions.filter(option => option.category === 'atual');
  const opcoesBrevemente = configOptions.filter(option => option.category === 'breve');
  const opcoesFuturas = configOptions.filter(option => option.category === 'futuro');

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: alpha(customTheme.palette.primary.main, 0.03) }}>
      <Sidebar
        sidebarOpen={sidebarOpen}
        customTheme={customTheme}
      />

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Header
          sidebarOpen={sidebarOpen}
                      handleSidebarToggle={toggleSidebar}
          customTheme={customTheme}
        />

        <Container maxWidth="xxl" sx={{ mt: 10, mb: 4 }}>
          {/* Breadcrumbs para navegação */}
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            aria-label="breadcrumb"
            sx={{ mb: 3 }}
          >
            <Link 
              color="inherit" 
              href="/dashboard" 
              onClick={(e) => {
                e.preventDefault();
                navigate('/dashboard');
              }}
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                textDecoration: 'none',
                color: 'text.primary',
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Dashboard
            </Link>
            <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
              <SettingsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Configurações
            </Typography>
          </Breadcrumbs>
          
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 4,
              border: `1px solid ${alpha(customTheme.palette.primary.main, 0.2)}`,
              background: theme => theme.palette.mode === 'dark'
                ? `linear-gradient(135deg, ${alpha(customTheme.palette.grey[900], 0.95)} 0%, ${alpha(customTheme.palette.grey[800], 0.9)} 100%)`
                : 'background.paper',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              textAlign: 'center',
              mb: 4 
            }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  background: theme => theme.palette.mode === 'dark'
                    ? `linear-gradient(90deg, ${customTheme.palette.primary.light} 0%, ${customTheme.palette.primary.main} 100%)`
                    : `linear-gradient(90deg, ${customTheme.palette.primary.main} 0%, ${customTheme.palette.primary.dark} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Central de Configurações
              </Typography>
              
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ 
                  maxWidth: '700px',
                  mb: 1
                }}
              >
                Bem-vindo à Central de Configurações do Sistema de Gerenciamento de Senhas. 
                Aqui você pode personalizar todos os aspectos do sistema para atender às necessidades da sua organização.
              </Typography>
            </Box>

            {/* Seção: Configurações Disponíveis */}
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2,
                mt: 4,
                fontWeight: 600,
                color: customTheme.palette.primary.main
              }}
            >
              Configurações Disponíveis
            </Typography>
            
            <Grid container spacing={3}>
              {opcoesAtuais.map((option) => (
                <Grid item xs={12} sm={6} md={4} key={option.title}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      border: `1px solid ${alpha(option.color, 0.2)}`,
                      opacity: option.disabled ? 0.6 : 1,
                      '&:hover': {
                        boxShadow: !option.disabled ? `0 8px 24px ${alpha(option.color, 0.25)}` : 'none',
                        transform: !option.disabled ? 'translateY(-4px)' : 'none',
                        border: `1px solid ${alpha(option.color, 0.4)}`,
                      }
                    }}
                  >
                    <CardActionArea 
                      onClick={() => handleOptionClick(option.path, option.disabled)}
                      disabled={option.disabled}
                      sx={{ 
                        height: '100%',
                        p: 1,
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box 
                          sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2,
                            color: option.color,
                          }}
                        >
                          <Box 
                            sx={{ 
                              display: 'flex',
                              p: 1.5,
                              borderRadius: '50%',
                              bgcolor: alpha(option.color, 0.1),
                              color: option.color,
                              mr: 2
                            }}
                          >
                            {option.icon}
                          </Box>
                          <Typography 
                            variant="h6" 
                            component="h2"
                            sx={{ fontWeight: 600 }}
                          >
                            {option.title}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {option.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            {/* Seção: Em Breve */}
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2,
                mt: 6,
                fontWeight: 600,
                color: 'text.secondary'
              }}
            >
              Em Breve
            </Typography>
            
            <Grid container spacing={3}>
              {opcoesBrevemente.map((option) => (
                <Grid item xs={12} sm={6} md={4} key={option.title}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      border: `1px solid ${alpha(option.color, 0.2)}`,
                      opacity: 0.7,
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box 
                        sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          mb: 2,
                          color: option.color,
                        }}
                      >
                        <Box 
                          sx={{ 
                            display: 'flex',
                            p: 1.5,
                            borderRadius: '50%',
                            bgcolor: alpha(option.color, 0.1),
                            color: option.color,
                            mr: 2
                          }}
                        >
                          {option.icon}
                        </Box>
                        <Typography 
                          variant="h6" 
                          component="h2"
                          sx={{ fontWeight: 600 }}
                        >
                          {option.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: 'block', 
                          mt: 2,
                          fontStyle: 'italic',
                          color: 'text.disabled'
                        }}
                      >
                        Disponível em breve
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            {/* Seção: Recursos Futuros */}
            {opcoesFuturas.length > 0 && (
              <>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2,
                    mt: 6,
                    fontWeight: 600,
                    color: 'text.secondary'
                  }}
                >
                  Recursos Futuros
                </Typography>
                
                <Grid container spacing={3}>
                  {opcoesFuturas.map((option) => (
                    <Grid item xs={12} sm={6} md={4} key={option.title}>
                      <Card 
                        sx={{ 
                          height: '100%',
                          borderRadius: 3,
                          transition: 'all 0.3s ease',
                          border: `1px solid ${alpha(option.color, 0.1)}`,
                          opacity: 0.6,
                          bgcolor: theme => theme.palette.mode === 'dark' 
                            ? alpha(theme.palette.background.paper, 0.4)
                            : alpha(theme.palette.background.paper, 0.7)
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box 
                            sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              mb: 2,
                              color: option.color,
                            }}
                          >
                            <Box 
                              sx={{ 
                                display: 'flex',
                                p: 1.5,
                                borderRadius: '50%',
                                bgcolor: alpha(option.color, 0.1),
                                color: option.color,
                                mr: 2
                              }}
                            >
                              {option.icon}
                            </Box>
                            <Typography 
                              variant="h6" 
                              component="h2"
                              sx={{ fontWeight: 600 }}
                            >
                              {option.title}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {option.description}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block', 
                              mt: 2,
                              fontStyle: 'italic',
                              color: 'text.disabled'
                            }}
                          >
                            Planejado para versões futuras
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default Configuracoes; 